import React from 'react';
import { MapPin, Clock, ArrowUpRight, Trophy, Star, BadgeCheck } from 'lucide-react';
import { siteConfig, experience, tools, freelancerStats } from '../data/mock';

const AboutSection = () => (
  <section id="about" className="py-24 lg:py-32 relative overflow-hidden" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-5" data-reveal>
          <div className="relative">
            <div className="absolute -inset-2 rounded-[28px] border border-dashed border-[#10B981]/60" />
            <div className="relative rounded-[24px] overflow-hidden aspect-square shadow-[0_40px_80px_-30px_rgba(11,19,43,0.35)]">
              <img src="/images/usama-working.jpg" alt="Usama Touseef working at his desk" className="w-full h-full object-cover" data-testid="about-portrait-image" />
              <div className="absolute top-4 left-4 pill !bg-white/90 backdrop-blur"><MapPin className="w-3 h-3 text-green" /> {siteConfig.location}</div>
              <div className="absolute bottom-4 left-4 pill !bg-white/90 backdrop-blur"><Clock className="w-3 h-3 text-green" /> {siteConfig.timezone} · async-friendly</div>
            </div>
          </div>

          <a
            href={siteConfig.freelancerProfile}
            target="_blank"
            rel="noopener noreferrer"
            className="panel panel-hover block p-5 bg-white"
            data-testid="about-freelancer-card"
          >
            <div className="flex items-center justify-between mb-4">
              <img src={freelancerStats.preferredBadge} alt="Preferred Freelancer" className="h-8" />
              <span className="flex items-center gap-1 text-xs font-medium text-slate"><BadgeCheck className="w-4 h-4 text-green" /> Verified profile <ArrowUpRight className="w-3.5 h-3.5" /></span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="font-display text-2xl font-extrabold text-ink flex items-center gap-1"><Trophy className="w-4 h-4 text-green" /> {freelancerStats.rank}</p>
                <p className="text-[11px] text-slate-2 leading-tight mt-1">{freelancerStats.rankOf}</p>
              </div>
              <div>
                <p className="font-display text-2xl font-extrabold text-ink flex items-center gap-1"><Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" /> {freelancerStats.rating}</p>
                <p className="text-[11px] text-slate-2 leading-tight mt-1">{freelancerStats.reviews} reviews</p>
              </div>
              <div>
                <p className="font-display text-2xl font-extrabold text-ink">{freelancerStats.onTime}</p>
                <p className="text-[11px] text-slate-2 leading-tight mt-1">on time & budget</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate font-mono">freelancer.com/u/usamatausif</p>
          </a>
        </div>

        <div className="lg:col-span-7">
          <div data-reveal>
            <p className="eyebrow mb-4">About · From: Usama</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-[1.05] mb-6">
              Hi, I'm {siteConfig.personName}. I've lived inside inboxes since 2017.
            </h2>
            <div className="space-y-4 text-slate text-base md:text-lg leading-relaxed max-w-2xl">
              <p>
                I help B2B companies reach new clients through cold email, email marketing and custom lead lists. I set up
                private email servers so you own your deliverability, and I scrape and verify leads that match your exact
                ideal customer.
              </p>
              <p>
                Most outreach fails on the boring parts — DNS records, warm-up, list quality. I obsess over those so the
                creative part (the copy) actually gets seen. Freelancer.com ranks me in the top 3% of 89 million freelancers,
                and my clients tend to come back for a second and third project.
              </p>
            </div>
          </div>

          <ol className="mt-12 relative border-l border-dashed border-[#CBD5E1] ml-2 space-y-8" data-testid="experience-timeline">
            {experience.map((x, i) => (
              <li key={x.role} data-reveal style={{ transitionDelay: `${i * 80}ms` }} className="relative pl-8">
                <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full bg-green ring-4 ring-[#FAFAF7]" />
                <p className="font-mono text-xs text-slate-2 tracking-wider mb-1">{x.period}</p>
                <h3 className="font-display text-lg font-bold text-ink">
                  {x.role} <span className="text-slate-2 font-medium">· {x.org}</span>
                </h3>
                <p className="text-slate text-sm leading-relaxed mt-1 max-w-xl">{x.detail}</p>
              </li>
            ))}
          </ol>

          <div className="mt-12" data-reveal>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-2 mb-4">Tools & stack</p>
            <div className="flex flex-wrap gap-2" data-testid="tools-list">
              {tools.map((t) => (
                <span key={t} className="pill !text-xs !py-1.5 !px-3 hover:border-[#10B981] hover:text-green transition-colors">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default AboutSection;
